import React from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';

interface TeamMember {
  name: string;
  role: string;
  image: string;
  video?: string;
  bio: string;
  skills: string[];
  linkedin: string;
  email?: string;
  website?: string;
  phone?: string;
  objectPosition?: string;
}

const team: TeamMember[] = [
  {
    name: 'Ian Broekman',
    role: 'Lead Developer',
    image: '/images/team/ian-broekman.png',
    video: '/images/team/ian.mp4',
    bio: 'Ian is a highly accomplished lead developer and design innovator, blending deep technical expertise with a strong creative vision. Formerly a co-founder and Chief Innovation/Design Officer in high-end automotive design, Ian transitioned his robust problem-solving skills and eye for user-centric detail into full-stack software development. He designs and builds premium, state-of-the-art web architectures and applications that deliver seamless, high-performance user experiences. His design-led development philosophy ensures that ProCreative\'s technical solutions are both visually stunning and structurally flawless.',
    skills: ['Full-Stack Development', 'React & Next.js', 'UI/UX Design', 'Software Engineering', 'System Architecture'],
    linkedin: 'https://www.linkedin.com/in/ianbroekman/',
    email: 'ian@procreative.co.za',
    objectPosition: 'center 18%'
  },
  {
    name: 'Rean Combrinck',
    role: 'Solutions Architect & Director',
    image: '/images/team/rean-combrinck.png',
    video: '/images/team/rean.mp4',
    bio: 'Rean is a technology strategist, solutions architect, and digital transformation leader with a passion for emerging technologies and their business impact. Bridging the gap between technology, strategy, and creativity, he designs and implements complex solutions utilizing AI, cloud computing, enterprise ERP/CRM integration, and decentralised networks. With a solid foundation in visual communication and digital strategy, Rean has spearheaded major systems integrations, digital transformation initiatives, and creative operations throughout his career, ensuring businesses are positioned for scale and efficiency.',
    skills: ['Solutions Architecture', 'Digital Strategy', 'Systems Integration', 'AI & Emerging Tech', 'Creative Operations'],
    linkedin: 'https://www.linkedin.com/in/rean-combrinck/',
    email: 'rean@procreative.co.za',
    website: 'https://reancombrinck.co.za',
    phone: '+27 74 581 2341',
    objectPosition: 'center 8%'
  }
];

const TeamPage: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-background dark:bg-dark-background border-b border-border/30 dark:border-dark-border/30">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Our People</span>
          <motion.h1 
            className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground dark:text-dark-foreground mt-2"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Meet Our <span className="font-serif italic text-accent font-light">Founding Partners</span>
          </motion.h1>
          <motion.p 
            className="mt-4 max-w-2xl mx-auto text-sm text-foreground/60 dark:text-dark-foreground/60 font-light leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Sovereign engineering, technical systems architecture, and design innovation driven by decades of combined experience.
          </motion.p>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 bg-background dark:bg-dark-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {team.map((member) => (
              <motion.div 
                key={member.name}
                className="flex flex-col bg-card dark:bg-[#0A1411] rounded-2xl overflow-hidden border border-border/60 dark:border-dark-border/60 hover:border-accent/40 dark:hover:border-dark-accent/40 transition-all duration-300 group hover:shadow-soft dark:hover:shadow-dark-card"
                variants={itemVariants}
              >
                {/* Image / Video Portrait */}
                <div className="relative h-[320px] sm:h-[380px] overflow-hidden bg-secondary dark:bg-[#0E1B17]">
                  {member.video ? (
                    <video
                      src={member.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      style={{ objectPosition: member.objectPosition || 'center' }}
                    />
                  ) : (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      loading="lazy"
                      style={{ objectPosition: member.objectPosition || 'center' }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h2 className="text-xl font-bold tracking-tight text-white">{member.name}</h2>
                    <p className="text-accent dark:text-dark-accent font-serif italic text-sm mt-0.5">{member.role}</p>
                  </div>
                </div>

                {/* Body Content */}
                <div className="flex-1 p-6 flex flex-col justify-between space-y-6">
                  <div className="space-y-6">
                    <p className="text-foreground/75 dark:text-dark-foreground/75 leading-relaxed text-sm font-sans font-light">
                      {member.bio}
                    </p>

                    {/* Expertise/Skills Tags */}
                    <div>
                      <h3 className="text-[10px] font-semibold tracking-wider uppercase text-foreground/40 dark:text-dark-foreground/45 mb-2.5">
                        Core Expertise
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {member.skills.map((skill) => (
                          <span 
                            key={skill}
                            className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-medium bg-secondary/60 dark:bg-dark-secondary/40 text-foreground/80 dark:text-dark-foreground/80 border border-border/40 dark:border-dark-border/40"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Social & Contact Connections */}
                  <div className="pt-4 border-t border-border/40 dark:border-dark-border/40 flex items-center justify-between">
                    <div className="flex space-x-4">
                      {/* LinkedIn */}
                      <a 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-foreground/40 hover:text-accent dark:text-dark-foreground/40 dark:hover:text-dark-accent transition-colors"
                        aria-label={`${member.name}'s LinkedIn Profile`}
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>

                      {/* Personal Website */}
                      {member.website && (
                        <a 
                          href={member.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-foreground/40 hover:text-accent dark:text-dark-foreground/40 dark:hover:text-dark-accent transition-colors"
                          aria-label={`${member.name}'s Personal Website`}
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                        </a>
                      )}

                      {/* Email */}
                      {member.email && (
                        <a 
                          href={`mailto:${member.email}`}
                          className="text-foreground/40 hover:text-accent dark:text-dark-foreground/40 dark:hover:text-dark-accent transition-colors"
                          aria-label={`Email ${member.name}`}
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L22 8m-9 11h.01M21 16V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2h14a2 2 0 002-2z" />
                          </svg>
                        </a>
                      )}

                      {/* Phone */}
                      {member.phone && (
                        <a 
                          href={`tel:${member.phone.replace(/\s+/g, '')}`}
                          className="text-foreground/40 hover:text-accent dark:text-dark-foreground/40 dark:hover:text-dark-accent transition-colors"
                          aria-label={`Call ${member.name}`}
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0A1411] py-16 text-center border-t border-[#111F1B]">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Have an operations engineering challenge?
          </h2>
          <p className="text-sm text-white/70 font-sans font-light">
            Consult with our partners directly to audit your current systems and design custom automations.
          </p>
          <div className="pt-4 flex justify-center">
            <Button href="/contact" variant="accent" size="lg" className="rounded-full px-8 font-semibold tracking-wide text-xs uppercase">
              Get In Touch
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TeamPage;
